import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  HiOutlineBan,
  HiOutlineCash,
  HiOutlineClock,
  HiOutlineCreditCard,
  HiOutlineExclamation,
  HiOutlineOfficeBuilding,
  HiOutlineShieldExclamation,
  HiOutlineTrendingUp,
  HiOutlineUserGroup,
} from "react-icons/hi";
import { PaCompactMetricChip } from "components/platform-admin";
import { buildDashboardWidgetSections } from "./platformDashboard.utils";
import { DASHBOARD_SECTIONS, hasVisibleSection } from "./platformDashboardCapabilities.utils";

const PLATFORM_ICONS = {
  total_tenants: HiOutlineOfficeBuilding,
  active_tenants: HiOutlineOfficeBuilding,
  on_trial: HiOutlineClock,
  suspended: HiOutlineBan,
  read_only: HiOutlineShieldExclamation,
  prospects: HiOutlineUserGroup,
};

const FINANCIAL_ICONS = {
  paying_tenants: HiOutlineCreditCard,
  estimated_mrr: HiOutlineTrendingUp,
  collected_30d: HiOutlineCash,
  collected_all_time: HiOutlineCash,
  outstanding: HiOutlineExclamation,
  failed_payments: HiOutlineExclamation,
};

const ChipSkeleton = () => (
  <div className="h-[52px] min-w-[7rem] flex-1 animate-pulse rounded-lg border border-gray-200 bg-gray-100" />
);

const WidgetRow = ({ title, action, children }) => {
  if (!children || (Array.isArray(children) && children.length === 0)) {
    return null;
  }

  return (
    <div className="mb-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</p>
        {action}
      </div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
};

const PlatformDashboardSummary = ({
  stats = null,
  billingPayload = null,
  capabilities = {},
  visibleSections = [],
  loading = false,
  billingLoading = false,
}) => {
  const sections = useMemo(
    () =>
      buildDashboardWidgetSections({
        stats: stats || {},
        capabilities: {
          ...capabilities,
          canListTenants:
            capabilities.canListTenants &&
            hasVisibleSection(visibleSections, DASHBOARD_SECTIONS.TENANTS),
          canViewProspects:
            capabilities.canViewProspects &&
            hasVisibleSection(visibleSections, DASHBOARD_SECTIONS.PROSPECTS),
        },
        billingPayload: capabilities.canViewBilling ? billingPayload : null,
      }),
    [billingPayload, capabilities, stats, visibleSections]
  );

  const showPlatform =
    (capabilities.canListTenants &&
      hasVisibleSection(visibleSections, DASHBOARD_SECTIONS.TENANTS)) ||
    (capabilities.canViewProspects &&
      hasVisibleSection(visibleSections, DASHBOARD_SECTIONS.PROSPECTS));
  const showFinancial = capabilities.canViewBilling;

  return (
    <>
      {showPlatform ? (
        <WidgetRow title="Platform">
          {loading
            ? Array.from({ length: capabilities.canListTenants ? 5 : 1 }).map((_, index) => (
                <ChipSkeleton key={index} />
              ))
            : sections.platform.map((widget) => (
                <PaCompactMetricChip
                  key={widget.id}
                  {...widget}
                  icon={PLATFORM_ICONS[widget.id]}
                />
              ))}
        </WidgetRow>
      ) : null}

      {showFinancial ? (
        <WidgetRow
          title="Stripe & revenue"
          action={
            <Link to="/billing" className="text-xs font-medium text-indigo-600 hover:text-indigo-700">
              Financial overview →
            </Link>
          }
        >
          {billingLoading
            ? Array.from({ length: 6 }).map((_, index) => <ChipSkeleton key={index} />)
            : sections.financial.map((widget) => (
                <PaCompactMetricChip
                  key={widget.id}
                  {...widget}
                  icon={FINANCIAL_ICONS[widget.id]}
                />
              ))}
        </WidgetRow>
      ) : null}
    </>
  );
};

export default React.memo(PlatformDashboardSummary);
